import { css } from '@emotion/css';
import authApi from 'apis/authApi';
import userApi from 'apis/userApi';
import { RootState, useAppDispatch, useAppSelector } from 'cache/store';
import classNames from 'classnames';
import AuthHeader from 'components/auth-header/AuthHeader';
import ForgotPassword from 'components/forgot-pw/forgot-pw';
import HeaderMenu from 'components/header-menu/HeaderMenu';
import LoadingIcon from 'components/loading/LoadingIcon';
import Modal from 'components/modal/Modal';
import UpdateUser from 'components/update-user/UpdateUser';
import { KEY_HASH, REG_SECRET_KEY } from 'constants/common';
import globalStyle from 'constants/global.style';
import { cacheUserInfo, login, logout, rememberAccountAction, updateAgentType } from 'features/authSlice';
import { toggleShowFirstNotice, updateMessageUnread } from 'features/msgSystemSlice';
import { LoginPayload } from 'models/authModel';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { encode } from 'utils/base64';
import { sha256Hash } from 'utils/function';
import AccountHeader from './account-header/AccountHeader';
import styles from './header.styles';
import LoginHeader from './login-header/LoginHeader';

interface UserData {
    username: string;
    balance: string;
}

function Header() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState<UserData>({} as UserData);
    const { isLogged, userInfo } = useAppSelector((state: RootState) => state.auth);
    const { msgUnread } = useAppSelector((state: RootState) => state.system);
    const { infoApp } = useAppSelector((state: RootState) => state.config);
    const [showAuth, setShowAuth] = useState(false);
    const [showForgotPW, setShowForgotPW] = useState(false);
    const [showAccountInfo, setShowAccountInfo] = useState(false);
    const [isReloadBalance, setIsReloadBalance] = useState(false);
    const [showUpdateUser, setShowUpdateUser] = useState(false);
    const [loginName, setLoginName] = useState('');

    useEffect(() => {
        if (userInfo && isLogged) {
            setUserData({
                username: userInfo.loginname,
                balance: userInfo.balance,
            });
        }
    }, [userInfo, isLogged]);

    useEffect(() => {
        if (isLogged) {
            const getMsgUnread = async () => {
                try {
                    const { data } = await userApi.personMessageSearch({});
                    if (!data.msg) {
                        const msgUnreadList: any[] = data.reslist.filter((msg: any) => msg.viewtimes === 0);
                        dispatch(updateMessageUnread(msgUnreadList.length));
                    }
                } catch (error) {
                    console.log(error);
                }
            };
            getMsgUnread();
        }
    }, [isLogged]);

    useEffect(() => {
        const intervalBalance = setInterval(() => {
            if (isLogged) {
                refreshBalance();
            }
        }, 10000);

        return () => {
            clearInterval(intervalBalance);
        };
    }, [isLogged]);

    const submitLogin = async (payload: LoginPayload, showAlertSuccess: boolean, closeModal: boolean, rememberAccount?: boolean) => {
        setLoading(true);
        try {
            const { loginFollow } = infoApp;

            if (String(loginFollow) !== '0') {
                try {
                    const ts = Date.now();
                    const username = payload.username.toLowerCase().trim();
                    const uuid = sha256Hash(`${username}${payload.password}${ts}${REG_SECRET_KEY}`);
                    const loginCheck = await authApi.loginCheckAccount({ username, password: payload.password, ts, uuid });

                    if (!loginCheck.data.success) {
                        const signUpPayload = {
                            agentid: '10020',
                            id: '1635310',
                            email: '',
                            nickname: username,
                            phone: '',
                            pwd: payload.password,
                            un: username,
                            zjpwd: payload.password,
                            loginname: username,
                            enc: sha256Hash(`${username}${KEY_HASH}${payload.password}${username}`),
                        };
                        await authApi.signUp(signUpPayload);
                    }
                } catch (error) {
                    console.log('loginCheckAccount failed, proceeding with loginGetToken', error);
                }
            }

            const { data } = await authApi.loginGetToken(payload);
            if (data.msg === '') {
                dispatch(login(data.data));

                if (closeModal) {
                    setShowAuth(false);
                }

                if (showAlertSuccess) {
                    toast.success('Đăng nhập thành công!');
                }
                getUserInfo(payload, rememberAccount);
            } else if (data.msg === '-1') {
                setShowUpdateUser(true);
                setLoginName(data.user.loginname);
            } else {
                toast.error(data.msg);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const getUserInfo = async (payload: LoginPayload, rememberAccount?: boolean) => {
        const { username, password } = payload;

        try {
            const { data } = await authApi.getUserInfo();
            if (!data.errorCode) {
                dispatch(cacheUserInfo(data));

                //cache agent type account
                let agentTypeUser = null;
                if (data.usertype === -2) {
                    agentTypeUser = 1;
                } else if (data['param9'] && data['param9'] !== '') {
                    agentTypeUser = 2;
                }
                dispatch(updateAgentType(agentTypeUser));

                //cache remember account
                if (rememberAccount) {
                    const rememberData = {
                        account: encode(`${username}|${password}`),
                        isRemember: true,
                    };
                    dispatch(rememberAccountAction(rememberData));
                } else {
                    dispatch(rememberAccountAction({ isRemember: false, account: null }));
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const changeWalletPage = (path: 'wl-depo' | 'wl-wdr') => {
        navigate(`/account?mode=${path}`);
    };

    const onLogout = async () => {
        try {
            await authApi.logoutByToken();
            dispatch(logout());
            dispatch(toggleShowFirstNotice(false));
            navigate('/');
        } catch (error) {
            console.log(error);
        }
    };

    const refreshBalance = async () => {
        if (!(!isLogged || isReloadBalance)) {
            setIsReloadBalance(true);
            try {
                const { data } = await authApi.getUserInfo();
                if (!data.errorCode) dispatch(cacheUserInfo(data));
            } catch (error) {
                console.log(error);
            } finally {
                setTimeout(() => setIsReloadBalance(false), 1000);
            }
        }
    };

    return (
        <>
            <div className={styles.wrap}>
                <div className={globalStyle.container}>
                    <div className={styles.content}>
                        <div className={styles.logo} onClick={() => navigate('/')}>
                            <img src={require('assets/images/logos/logo.png')} alt="" />
                        </div>
                        <div className="header-right">
                            {!isLogged ? (
                                <LoginHeader onShowForgotPW={() => setShowForgotPW(true)} onLogin={submitLogin} onShowAuth={() => navigate('/reg')} />
                            ) : (
                                <div className={styles.userHeader}>
                                    <div className="headerTop">
                                        <button type="button" className={styles.balancePill} onClick={refreshBalance} aria-label="Làm mới số dư">
                                            <p className={styles.balanceText}>{`${new Intl.NumberFormat().format(Number(userData.balance) || 0)} VNĐ`}</p>
                                        </button>
                                        <div className={styles.wrapBtnAccount} onMouseEnter={() => setShowAccountInfo(true)} onMouseLeave={() => setShowAccountInfo(false)}>
                                            <button type="button" className={classNames(`${styles.btnLeft} ${styles.btnAccount}`, { show: showAccountInfo })}>
                                                <img src={require('assets/images/logos/default-avatar.png')} crossOrigin="anonymous" style={{ borderRadius: 100, width: 20, height: 20 }} alt="" />
                                                <p className={styles.username}>{userData.username}</p>
                                                <img className={`${styles.arrow} arrow-icon`} src={require('assets/images/header-figma/dropdown.svg')} alt="" />
                                            </button>
                                            <AccountHeader show={showAccountInfo} onHide={() => setShowAccountInfo(false)} />
                                        </div>

                                        <button type="button" className={styles.notificationButton} onClick={() => navigate('/account?mode=noti-inbox')}>
                                            <img className={styles.notification} src={require('assets/images/header-figma/notification.png')} alt="Thông báo" />
                                            {msgUnread > 0 && <span className={styles.countUnreadMsg}>{msgUnread > 9 ? '9+' : msgUnread}</span>}
                                        </button>
                                        <button type="button" className={classNames(styles.walletAction, styles.depositAction)} onClick={() => changeWalletPage('wl-depo')}>Nạp Tiền</button>
                                        <button type="button" className={classNames(styles.walletAction, styles.withdrawAction)} onClick={() => changeWalletPage('wl-wdr')}>Rút Tiền</button>
                                        <button type="button" className={classNames(styles.walletAction, styles.logoutAction)} onClick={onLogout}>Thoát</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <HeaderMenu />
            </div>

            <Modal isShow={showAuth} onClose={() => setShowAuth(false)} backdropColor="rgba(255,255,255, 0.7)" width="1050px">
                <AuthHeader onClose={() => setShowAuth(false)} onLogin={submitLogin} />
            </Modal>

            {/* popup update user account */}
            <Modal isShow={showUpdateUser} onClose={() => setShowUpdateUser(false)} width="520px">
                <UpdateUser onClose={() => setShowUpdateUser(false)} loginName={loginName} />
            </Modal>

            {/* popup forgot password */}
            <Modal isShow={showForgotPW} onClose={() => setShowForgotPW(false)} width="600px">
                <ForgotPassword onClose={() => setShowForgotPW(false)} />
            </Modal>

            {loading && <LoadingIcon />}
        </>
    );
}

export default Header;
